import React from 'react';
import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileIconTileProps {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Background color (hex or CSS color) */
  backgroundColor: string;
  /** Icon color (defaults to white) */
  iconColor?: string;
  /** Size variant */
  size?: 'small' | 'large';
  /** Additional className for the container */
  className?: string;
  /** Additional icon className */
  iconClassName?: string;
  /** Inline styles for the container */
  style?: React.CSSProperties;
}

/**
 * FileIconTile component - displays a Lucide icon within a colored rounded square tile
 * Similar to IconTile but accepts React component icons instead of icon name strings
 *
 * @example
 * // Small tile (32x32) with file icon
 * <FileIconTile icon={FileText} backgroundColor="#f43f5e" size="small" />
 *
 * @example
 * // Large tile (40x40) with custom positioning
 * <FileIconTile
 *   icon={Image}
 *   backgroundColor="#eab308"
 *   size="large"
 *   className="absolute"
 *   style={{ left: '20px', zIndex: 1 }}
 * />
 */
export const FileIconTile: React.FC<FileIconTileProps> = ({
  icon: Icon,
  backgroundColor,
  iconColor = '#ffffff',
  size = 'small',
  className,
  iconClassName,
  style,
}) => {
  const sizeConfig = {
    small: {
      container: 'w-8 h-8', // 32px
      icon: iconClassName || 'w-5! h-5!',
    },
    large: {
      container: 'w-10 h-10', // 40px
      icon: iconClassName || 'w-6 h-6',
    },
  };

  const config = sizeConfig[size];

  const styleHasBackground =
    style != null &&
    (style.background !== undefined || style.backgroundImage !== undefined);

  const containerStyle = {
    ...(backgroundColor && !styleHasBackground
      ? {
          background: `linear-gradient(to bottom, color-mix(in srgb, ${backgroundColor} 90%, white), ${backgroundColor})`,
        }
      : {}),
    ...style,
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-lg shrink-0',
        config.container,
        className,
      )}
      style={containerStyle}
    >
      <Icon
        className={config.icon}
        style={iconColor ? { color: iconColor } : undefined}
      />
    </div>
  );
};
